package top.wcpe.allincore.ui.runtime.ir.call

import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import kotlin.jvm.JvmStatic

/**
 * 调用分派器门面（Phase2 拆分后的瘦门面）。
 *
 * 集中处理 DSL 中所有"调用"形态的运行期分派，Phase2 起按职责拆分为四个 internal
 * 协作者，本类只保留对外 11 个 @JvmStatic 方法作为稳定 ABI，方法体纯转发、零业务逻辑：
 * - 内建调用 / net.subscribe → [BuiltinCallEvaluator]
 *   （evalBuiltinCall / evalNetSubscribe / wrapSubscribeCallable）
 * - 可调用对象 / CallExpr → [CallableInvoker]
 *   （makeLambdaValue / invokeLambda / invokeCallable / evalCallExpr）
 * - 成员方法调用 → [MemberCallDispatcher]
 *   （evalMemberCall / invokeMemberCall）
 *
 * 跨职责共享的实参强转助手归 [CallArgs]。协作者依赖 DAG（无环）：
 * CallArgs ← CallableInvoker；CallArgs + CallableInvoker ← BuiltinCallEvaluator；
 * CallArgs（+ CallableInvoker timer 回调）← MemberCallDispatcher。
 *
 * 这 11 个方法被 ExprEvaluator / IrInterpreter / EventBindingDispatcher 等跨包消费，
 * 签名 / @JvmStatic / 参数名 / 返回类型一字不可改。
 */
object CallDispatcher {

    /** 内建函数调用分派（RFC §3.3）。详见 [BuiltinCallEvaluator.evalBuiltinCall]。 */
    @JvmStatic
    fun evalBuiltinCall(call: ExprNode.BuiltinCall, ctx: EvalContext): Any? =
        BuiltinCallEvaluator.evalBuiltinCall(call, ctx)

    /** 求值 `net.subscribe(...)` IR 节点。详见 [BuiltinCallEvaluator.evalNetSubscribe]。 */
    @JvmStatic
    fun evalNetSubscribe(node: ExprNode.NetSubscribe, ctx: EvalContext): Any =
        BuiltinCallEvaluator.evalNetSubscribe(node, ctx)

    /** 把"DSL 可调用对象"包装为订阅 UiEventHandler。详见 [BuiltinCallEvaluator.wrapSubscribeCallable]。 */
    @JvmStatic
    fun wrapSubscribeCallable(
        callable: Any?,
        ctx: EvalContext,
        namespace: String,
        topic: String,
        hint: String,
    ): UiEventHandler<Map<String, Any?>> =
        BuiltinCallEvaluator.wrapSubscribeCallable(callable, ctx, namespace, topic, hint)

    /** 构造 LambdaValue。详见 [CallableInvoker.makeLambdaValue]。 */
    @JvmStatic
    fun makeLambdaValue(lam: ExprNode.Lambda, ctx: EvalContext): LambdaValue = CallableInvoker.makeLambdaValue(lam, ctx)

    /** 调用 lambda。详见 [CallableInvoker.invokeLambda]。 */
    @JvmStatic
    fun invokeLambda(lam: LambdaValue, argValues: List<Any?>, ctx: EvalContext): Any? =
        CallableInvoker.invokeLambda(lam, argValues, ctx)

    /** M5-b：带命名实参的 lambda 调用。详见 [CallableInvoker.invokeLambda]。 */
    @JvmStatic
    fun invokeLambda(lam: LambdaValue, argValues: List<Any?>, namedValues: Map<String, Any?>, ctx: EvalContext): Any? =
        CallableInvoker.invokeLambda(lam, argValues, namedValues, ctx)

    /** 通用调用表达式分派。详见 [CallableInvoker.evalCallExpr]。 */
    @JvmStatic
    fun evalCallExpr(call: ExprNode.CallExpr, ctx: EvalContext): Any? = CallableInvoker.evalCallExpr(call, ctx)

    /** 把任意运行时值当作可调用对象分派。详见 [CallableInvoker.invokeCallable]。 */
    @JvmStatic
    fun invokeCallable(callee: Any?, argValues: List<Any?>, ctx: EvalContext, hint: String): Any? =
        CallableInvoker.invokeCallable(callee, argValues, ctx, hint)

    /** M5-b：带命名实参的可调用对象分派。详见 [CallableInvoker.invokeCallable]。 */
    @JvmStatic
    fun invokeCallable(
        callee: Any?,
        argValues: List<Any?>,
        namedValues: Map<String, Any?>,
        ctx: EvalContext,
        hint: String,
    ): Any? = CallableInvoker.invokeCallable(callee, argValues, namedValues, ctx, hint)

    /** 分派 MemberCall。详见 [MemberCallDispatcher.evalMemberCall]。 */
    @JvmStatic
    fun evalMemberCall(call: ExprNode.MemberCall, ctx: EvalContext): Any? =
        MemberCallDispatcher.evalMemberCall(call, ctx)

    /** 抽出真正的方法分派，供 MemberCall / SafeMemberCall 复用。详见 [MemberCallDispatcher.invokeMemberCall]。 */
    @JvmStatic
    fun invokeMemberCall(target: Any?, method: String, argNodes: List<ExprNode>, ctx: EvalContext): Any? =
        MemberCallDispatcher.invokeMemberCall(target, method, argNodes, ctx)
}
